import React from 'react';
import { DayTemplateShift } from '@/types';
import { ShiftStatus, SHIFT_STATUS_CONFIG } from '@/lib/scheduling/shift-status-types';

interface DayTemplatePreviewProps {
  shifts: DayTemplateShift[];
  width?: number; // Timeline width in pixels
}

const DayTemplatePreview: React.FC<DayTemplatePreviewProps> = ({ shifts, width = 105 }) => {
  const [actualWidth, setActualWidth] = React.useState(width);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (containerRef.current) {
      setActualWidth(containerRef.current.offsetWidth);
    }
  }, []);

  // Calculate total duration for display purposes only (using extended 3-hour boundaries)
  const calculateTotalDuration = () => {
    if (shifts.length === 0) return 0;

    // Find the earliest start time and latest end time
    let actualEarliestMinutes = Infinity;
    let actualLatestMinutes = 0;

    shifts.forEach(shift => {
      const [startHour, startMinute] = shift.startTime.split(':').map(Number);
      const [endHour, endMinute] = shift.endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMinute;
      let endMinutes = endHour * 60 + endMinute;

      // Handle shifts that cross midnight
      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60; // Add 24 hours
      }

      actualEarliestMinutes = Math.min(actualEarliestMinutes, startMinutes);
      actualLatestMinutes = Math.max(actualLatestMinutes, endMinutes);
    });

    // Extend to 3-hour boundaries for consistent timeline display
    const earliestMinutes = Math.floor(actualEarliestMinutes / 180) * 180;
    const latestMinutes = Math.ceil(actualLatestMinutes / 180) * 180;

    return latestMinutes - earliestMinutes;
  };

  // Format time for display (24-hour format)
  const formatTime = (timeString: string) => {
    const [hour, minute] = timeString.split(':').map(Number);
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  // Get extended time range for the day (to 3-hour boundaries)
  const getTimeRange = () => {
    if (shifts.length === 0) return null;

    let actualEarliestMinutes = Infinity;
    let actualLatestMinutes = 0;

    shifts.forEach(shift => {
      const [startHour, startMinute] = shift.startTime.split(':').map(Number);
      const [endHour, endMinute] = shift.endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMinute;
      let endMinutes = endHour * 60 + endMinute;

      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60;
      }

      actualEarliestMinutes = Math.min(actualEarliestMinutes, startMinutes);
      actualLatestMinutes = Math.max(actualLatestMinutes, endMinutes);
    });

    // Extend to 3-hour boundaries
    const earliestMinutes = Math.floor(actualEarliestMinutes / 180) * 180;
    const latestMinutes = Math.ceil(actualLatestMinutes / 180) * 180;

    const startHour = Math.floor(earliestMinutes / 60);
    const startMin = earliestMinutes % 60;
    const endHour = Math.floor(latestMinutes / 60) % 24;
    const endMin = latestMinutes % 60;

    const startTime = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
    const endTime = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;

    return {
      start: startTime,
      end: endTime,
      startFormatted: formatTime(startTime),
      endFormatted: formatTime(endTime)
    };
  };

  // Create enhanced multi-row timeline visualization
  const renderDayPreview = () => {
    if (shifts.length === 0) return null;

    // Find bounds for relative positioning - first get actual shift boundaries
    let actualEarliestMinutes = Infinity;
    let actualLatestMinutes = 0;

    const shiftPositions = shifts.map((shift, index) => {
      const [startHour, startMinute] = shift.startTime.split(':').map(Number);
      const [endHour, endMinute] = shift.endTime.split(':').map(Number);

      const startMinutes = startHour * 60 + startMinute;
      let endMinutes = endHour * 60 + endMinute;

      if (endMinutes <= startMinutes) {
        endMinutes += 24 * 60;
      }

      actualEarliestMinutes = Math.min(actualEarliestMinutes, startMinutes);
      actualLatestMinutes = Math.max(actualLatestMinutes, endMinutes);

      return { startMinutes, endMinutes, shift, originalIndex: index };
    });

    // Extend to 3-hour boundaries (180 minutes = 3 hours)
    const earliestMinutes = Math.floor(actualEarliestMinutes / 180) * 180;
    const latestMinutes = Math.ceil(actualLatestMinutes / 180) * 180;

    const totalSpan = latestMinutes - earliestMinutes;
    const timelineWidth = width; // Use the width prop
    const timeRange = getTimeRange();

    // Algorithm to assign shifts to rows (avoid overlaps)
    const assignShiftsToRows = () => {
      const rows: Array<typeof shiftPositions> = [];

      // Sort shifts by start time
      const sortedShifts = [...shiftPositions].sort((a, b) => a.startMinutes - b.startMinutes);

      for (const shift of sortedShifts) {
        let assignedToRow = false;

        // Try to find an existing row where this shift doesn't overlap
        for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
          const row = rows[rowIndex];
          const hasOverlap = row.some(existingShift =>
            !(shift.endMinutes <= existingShift.startMinutes || shift.startMinutes >= existingShift.endMinutes)
          );

          if (!hasOverlap) {
            row.push(shift);
            assignedToRow = true;
            break;
          }
        }

        // If no existing row works, create a new row
        if (!assignedToRow) {
          rows.push([shift]);
        }
      }

      return rows;
    };

    const shiftRows = assignShiftsToRows();
    // Scale row height proportionally with width (8px for 105px width, up to 20px for larger widths)
    const rowHeight = Math.min(Math.max(8, Math.floor(actualWidth / 105 * 8)), 20);
    const rowSpacing = Math.max(2, Math.floor(rowHeight / 4));
    const totalHeight = shiftRows.length * rowHeight + (shiftRows.length - 1) * rowSpacing;

    // Generate hourly grid marks with 3-hour labels (no half-hour marks)
    const generateHourlyGrid = () => {
      const gridLines = [];
      const startHour = Math.floor(earliestMinutes / 60);
      const endHour = Math.ceil(latestMinutes / 60);

      for (let hour = startHour; hour <= endHour; hour++) {
        const hourMinutes = hour * 60;

        // Add full hour marks only
        if (hourMinutes >= earliestMinutes && hourMinutes <= latestMinutes) {
          const left = ((hourMinutes - earliestMinutes) / totalSpan) * actualWidth;
          const isThreeHourMark = hour % 3 === 0; // Check if divisible by 3

          gridLines.push({
            hour,
            minute: 0,
            left,
            isFullHour: true,
            isThreeHourMark,
            display: `${hour.toString().padStart(2, '0')}:00`,
            labelDisplay: hour.toString() // Just the hour number for labels
          });
        }
      }

      return gridLines.sort((a, b) => a.left - b.left);
    };

    const hourlyGridLines = generateHourlyGrid();

    // Scale label container height and font size based on width
    const labelHeight = Math.min(Math.max(14, Math.floor(actualWidth / 105 * 14)), 24);
    const labelTextSize = actualWidth > 200 ? 'text-sm' : 'text-xs';

    return (
      <div className="w-full max-w-full">
        {/* 3-hour time labels */}
        <div ref={containerRef} className="relative mb-1 w-full max-w-full" style={{ height: `${labelHeight}px` }}>
          {hourlyGridLines
            .filter(gridLine => gridLine.isThreeHourMark)
            .map((gridLine, index) => {
              // Calculate if label would overflow on left or right
              const labelLeft = gridLine.left;
              const isNearStart = labelLeft < 20;
              const isNearEnd = labelLeft > actualWidth - 20;

              return (
                <div
                  key={`label-${index}`}
                  className={`absolute ${labelTextSize} text-gray-500 dark:text-gray-400`}
                  style={{
                    left: isNearStart ? '0px' : isNearEnd ? `${actualWidth - 20}px` : `${labelLeft}px`,
                    transform: isNearStart ? 'none' : isNearEnd ? 'none' : 'translateX(-50%)',
                    top: '0px'
                  }}
                >
                  {gridLine.labelDisplay}
                </div>
              );
            })}
        </div>

        {/* Multi-row timeline visualization */}
        <div className="relative w-full max-w-full" style={{ height: `${Math.max(totalHeight + 4, 20)}px` }}>
          {/* Timeline background */}
          <div className="absolute inset-0" />

          {/* Hourly grid lines */}
          {hourlyGridLines.map((gridLine, index) => (
            <div
              key={`grid-${index}`}
              className={`absolute top-0 bottom-0 border-l ${
                gridLine.isThreeHourMark
                  ? 'border-gray-400 dark:border-gray-500 opacity-50' // Darker for 3-hour marks
                  : gridLine.isFullHour
                  ? 'border-gray-300 dark:border-gray-600 opacity-40'
                  : 'border-gray-200 dark:border-gray-700 opacity-25'
              }`}
              style={{ left: `${gridLine.left}px` }}
              title={gridLine.display}
            />
          ))}

          {/* Shift bars in rows */}
          {shiftRows.map((row, rowIndex) => {
            const rowTop = rowIndex * (rowHeight + rowSpacing) + 2;

            return row.map((shiftPos) => {
              const left = ((shiftPos.startMinutes - earliestMinutes) / totalSpan) * actualWidth;
              const shiftWidth = ((shiftPos.endMinutes - shiftPos.startMinutes) / totalSpan) * actualWidth;

              // Use status-based color from shift-status-styles.ts (same as time tracking badges)
              const status = (shiftPos.shift.status as ShiftStatus) || ShiftStatus.UNASSIGNED;
              const colorClass = SHIFT_STATUS_CONFIG[status]?.color?.background || 'bg-gray-500/60';
              // Scale minimum width proportionally (3px for small, up to 8px for large)
              const minWidth = Math.max(3, Math.floor(actualWidth / 105 * 3));
              // Ensure the bar doesn't exceed timeline width
              const maxAllowedWidth = actualWidth - left;
              const finalWidth = Math.min(Math.max(shiftWidth, minWidth), maxAllowedWidth);

              // Show text on bars when bar is wide enough - now showing initials for smaller bars
              const showText = finalWidth >= 25 && rowHeight >= 12;
              // Dynamic text size based on bar width
              const textSize = finalWidth >= 45 ? 'text-[10px]' : 'text-[8px]';

              // Determine what text to show based on bar width
              const getDisplayText = () => {
                const name = shiftPos.shift.employeeName;
                if (!name) {
                  // For unassigned: show "UN" for small, "Unassigned" for large
                  return finalWidth >= 45 ? 'Unassigned' : 'UN';
                }
                // For assigned employees
                if (finalWidth >= 45) {
                  return name; // Full name (with smaller text for 45-79px)
                }
                // 25-44px: 2 initials
                return name
                  .split(' ')
                  .map(word => word[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2);
              };

              return (
                <div
                  key={`${rowIndex}-${shiftPos.originalIndex}`}
                  className={`absolute ${colorClass} rounded-sm transition-opacity hover:opacity-90 flex items-center justify-center px-1`}
                  style={{
                    left: `${left}px`,
                    width: `${finalWidth}px`,
                    height: `${rowHeight}px`,
                    top: `${rowTop}px`,
                  }}
                  title={`${shiftPos.shift.title}: ${formatTime(shiftPos.shift.startTime)} - ${formatTime(shiftPos.shift.endTime)}${shiftPos.shift.employeeRole ? ` (${shiftPos.shift.employeeRole})` : ''}`}
                >
                  {showText && (
                    <span className={`${textSize} font-semibold text-white drop-shadow-sm`}>
                      {getDisplayText()}
                    </span>
                  )}
                </div>
              );
            });
          })}
        </div>

        {/* Compact time range - keep for very short timelines */}
        {timeRange && hourlyGridLines.filter(g => g.isThreeHourMark).length === 0 && (
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 text-center">
            {timeRange.startFormatted} - {timeRange.endFormatted}
          </div>
        )}
      </div>
    );
  };

  return renderDayPreview();
};

export default DayTemplatePreview;
